import { useState } from 'react';
import { MessageSquare, ChevronDown, ChevronRight, CornerDownRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ReviewComment } from '@/types/review';

export type ReviewCommentWithReplies = ReviewComment & {
  replies?: ReviewComment[];
};

export interface ReviewCommentsCalloutProps {
  comments?: ReviewCommentWithReplies[];
  className?: string;
}

export default function ReviewCommentsCallout({
  comments = [],
  className,
}: ReviewCommentsCalloutProps) {
  const [expanded, setExpanded] = useState(true);
  const openComments = comments.filter(
    (c) => c.status === 'suggested' && c.target_type !== 'comment',
  );

  if (openComments.length === 0) return null;

  return (
    <div
      data-review-comments-callout
      className={cn(
        'rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 space-y-2',
        className,
      )}
    >
      <button
        type="button"
        aria-label="Toggle review comments"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-full items-center justify-between text-left text-xs font-medium text-amber-800 dark:text-amber-300"
      >
        <span className="inline-flex items-center gap-1.5">
          <MessageSquare className="h-3.5 w-3.5" />
          {`${openComments.length} ${openComments.length === 1 ? 'Review Comment' : 'Review Comments'}`}
        </span>
        {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
      </button>
      {expanded && (
        <div className="space-y-2 pt-1">
          {openComments.map((c) => (
            <div
              key={c.id}
              className="rounded border border-amber-500/20 bg-background/80 p-2.5 text-sm space-y-1.5"
            >
              <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{c.created_by || c.source}</span>
                {c.severity && (
                  <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[11px] font-medium text-amber-900 dark:text-amber-200">
                    {c.severity}
                  </span>
                )}
                {c.quality_check_type && (
                  <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[11px] font-medium text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                    {c.quality_check_type}
                  </span>
                )}
              </div>
              {c.description && <p className="text-sm whitespace-pre-wrap">{c.description}</p>}
              {c.replies && c.replies.length > 0 && (
                <div className="mt-1.5 space-y-1.5 border-l-2 border-amber-500/30 pl-3">
                  {c.replies.map((r) => (
                    <div key={r.id} className="space-y-0.5 text-xs">
                      <div className="flex flex-wrap items-center gap-1.5 text-muted-foreground">
                        <CornerDownRight className="h-3 w-3 shrink-0" />
                        <span className="font-medium text-foreground">
                          {r.created_by || r.source}
                        </span>
                      </div>
                      {r.description && (
                        <p className="pl-4 text-xs whitespace-pre-wrap text-foreground">
                          {r.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
